'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import ConsultationOptions from './ConsultationOptions';
import { trackConsultationFormStep, trackConsultationSubmit } from '@/lib/analytics';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  jobTitle: string;
  company: string;
  companySize: string;
  industry: string;
  region: string;
  consultationType: string;
  aiReadiness: string;
  primaryObjective: string;
  timeline: string;
  additionalInfo: string;
  preferredDate: string;
  preferredTime: string;
  timezone: string;
}

interface FormErrors {
  [key: string]: string;
}

const InquiryForm = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    company: '',
    companySize: '',
    industry: '',
    region: '',
    consultationType: 'strategic-briefing',
    aiReadiness: '',
    primaryObjective: '',
    timeline: '',
    additionalInfo: '',
    preferredDate: '',
    preferredTime: '',
    timezone: 'AEDT',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const companySizes = [
    { value: '1-50', label: '1-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '201-500', label: '201-500 employees' },
    { value: '501-1000', label: '501-1,000 employees' },
    { value: '1001-5000', label: '1,001-5,000 employees' },
    { value: '5001+', label: '5,001+ employees' },
  ];

  const industries = [
    'Financial Services',
    'Healthcare',
    'Manufacturing',
    'Retail & E-commerce',
    'Technology',
    'Professional Services',
    'Education',
    'Government',
    'Energy & Utilities',
    'Telecommunications',
    'Other',
  ];

  const regions = [
    { value: 'NSW', label: 'New South Wales' },
    { value: 'VIC', label: 'Victoria' },
    { value: 'QLD', label: 'Queensland' },
    { value: 'WA', label: 'Western Australia' },
    { value: 'SA', label: 'South Australia' },
    { value: 'TAS', label: 'Tasmania' },
    { value: 'ACT', label: 'Australian Capital Territory' },
    { value: 'NT', label: 'Northern Territory' },
    { value: 'NZ-North', label: 'New Zealand - North Island' },
    { value: 'NZ-South', label: 'New Zealand - South Island' },
  ];

  const aiReadinessLevels = [
    { value: 'exploring', label: 'Exploring - Initial research phase' },
    { value: 'planning', label: 'Planning - Developing AI strategy' },
    { value: 'piloting', label: 'Piloting - Testing AI solutions' },
    { value: 'implementing', label: 'Implementing - Active deployment' },
    { value: 'scaling', label: 'Scaling - Expanding AI capabilities' },
  ];

  const primaryObjectives = [
    'Operational Efficiency',
    'Customer Experience Enhancement',
    'Risk Management & Compliance',
    'Revenue Growth',
    'Cost Reduction',
    'Innovation & Competitive Advantage',
    'Data-Driven Decision Making',
    'Process Automation',
  ];

  const timelines = [
    { value: 'immediate', label: 'Immediate (Within 1 month)' },
    { value: '1-3months', label: '1-3 months' },
    { value: '3-6months', label: '3-6 months' },
    { value: '6-12months', label: '6-12 months' },
    { value: '12months+', label: '12+ months' },
  ];

  const timezones = [
    { value: 'AEDT', label: 'AEDT (Australian Eastern Daylight Time)' },
    { value: 'ACDT', label: 'ACDT (Australian Central Daylight Time)' },
    { value: 'AWST', label: 'AWST (Australian Western Standard Time)' },
    { value: 'NZDT', label: 'NZDT (New Zealand Daylight Time)' },
  ];

  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
      if (!formData.jobTitle.trim()) newErrors.jobTitle = 'Job title is required';
    }

    if (step === 2) {
      if (!formData.company.trim()) newErrors.company = 'Company name is required';
      if (!formData.companySize) newErrors.companySize = 'Company size is required';
      if (!formData.industry) newErrors.industry = 'Industry is required';
      if (!formData.region) newErrors.region = 'Region is required';
    }

    if (step === 3) {
      if (!formData.aiReadiness) newErrors.aiReadiness = 'AI readiness level is required';
      if (!formData.primaryObjective) newErrors.primaryObjective = 'Primary objective is required';
      if (!formData.timeline) newErrors.timeline = 'Implementation timeline is required';
    }

    if (step === 4) {
      if (!formData.preferredDate) newErrors.preferredDate = 'Preferred date is required';
      if (!formData.preferredTime) newErrors.preferredTime = 'Preferred time is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleConsultationTypeChange = (optionId: string) => {
    setFormData(prev => ({ ...prev, consultationType: optionId }));
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      const stepNames: Record<number, string> = {
        1: 'Personal Information',
        2: 'Company Details',
        3: 'AI Objectives',
        4: 'Schedule Consultation',
      };
      trackConsultationFormStep(currentStep + 1, stepNames[currentStep + 1] || `Step ${currentStep + 1}`);
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);

    try {
      const res = await fetch('/api/send-partnership-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to submit form');
      }

      trackConsultationSubmit({
        consultationType: formData.consultationType,
        industry: formData.industry,
        companySize: formData.companySize,
        region: formData.region,
      });

      setSubmitSuccess(true);
    } catch (error: any) {
      console.error('Failed to submit form:', error);
      alert('Failed to submit form. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg shadow-subtle p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-3/4"></div>
          <div className="space-y-4">
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div className="bg-card rounded-lg shadow-subtle p-8 md:p-12 text-center">
        <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircleIcon" size={48} className="text-success" />
        </div>
        <h2 className="text-3xl font-display font-semibold text-foreground mb-4">
          Consultation Request Received
        </h2>
        <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
          Thank you for your interest in partnering with Mindshift AI. Our team will review your consultation request and contact you within 24 hours to confirm your appointment and provide pre-consultation resources.
        </p>
        <div className="bg-muted/50 rounded-lg p-6 max-w-xl mx-auto mb-8">
          <h3 className="text-sm font-display font-semibold text-foreground mb-4">
            What Happens Next?
          </h3>
          <div className="space-y-3 text-left">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-semibold">
                1
              </div>
              <p className="text-sm text-muted-foreground">
                Consultation confirmation email with calendar invite
              </p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-semibold">
                2
              </div>
              <p className="text-sm text-muted-foreground">
                Pre-consultation resources tailored to your objectives
              </p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-semibold">
                3
              </div>
              <p className="text-sm text-muted-foreground">
                Strategic consultation with our AI governance experts
              </p>
            </div>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          A confirmation email has been sent to <span className="font-semibold text-foreground">{formData.email}</span>
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-subtle overflow-hidden">
      <div className="bg-gradient-to-r from-primary/10 to-accent/10 px-8 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-display font-semibold text-foreground">
            Partnership Inquiry Form
          </h2>
          <span className="text-sm font-medium text-muted-foreground">
            Step {currentStep} of 4
          </span>
        </div>
        <div className="flex space-x-2">
          {[1, 2, 3, 4].map((step) => (
            <div
              key={step}
              className={`flex-1 h-2 rounded-full transition-all duration-300 ${
                step <= currentStep ? 'bg-primary' : 'bg-muted'
              }`}
            ></div>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-8">
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-display font-semibold text-foreground mb-2">
                Contact Information
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Please provide your contact details so we can reach you regarding your consultation.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-foreground mb-2">
                  First Name <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.firstName ? 'border-destructive' : 'border-input'
                  } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
                  placeholder="John"
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-destructive">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-foreground mb-2">
                  Last Name <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.lastName ? 'border-destructive' : 'border-input'
                  } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
                  placeholder="Smith"
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-destructive">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Business Email <span className="text-destructive">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.email ? 'border-destructive' : 'border-input'
                } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
                placeholder="john.smith@company.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-destructive">{errors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                Phone Number <span className="text-destructive">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.phone ? 'border-destructive' : 'border-input'
                } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
                placeholder="+61 4XX XXX XXX"
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-destructive">{errors.phone}</p>
              )}
            </div>

            <div>
              <label htmlFor="jobTitle" className="block text-sm font-medium text-foreground mb-2">
                Job Title <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                id="jobTitle"
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.jobTitle ? 'border-destructive' : 'border-input'
                } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
                placeholder="Chief Technology Officer"
              />
              {errors.jobTitle && (
                <p className="mt-1 text-sm text-destructive">{errors.jobTitle}</p>
              )}
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-display font-semibold text-foreground mb-2">
                Organization Details
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Help us understand your organization to provide the most relevant consultation.
              </p>
            </div>

            <div>
              <label htmlFor="company" className="block text-sm font-medium text-foreground mb-2">
                Company Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.company ? 'border-destructive' : 'border-input'
                } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
                placeholder="Acme Corporation"
              />
              {errors.company && (
                <p className="mt-1 text-sm text-destructive">{errors.company}</p>
              )}
            </div>

            <div>
              <label htmlFor="companySize" className="block text-sm font-medium text-foreground mb-2">
                Company Size <span className="text-destructive">*</span>
              </label>
              <select
                id="companySize"
                name="companySize"
                value={formData.companySize}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.companySize ? 'border-destructive' : 'border-input'
                } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
              >
                <option value="">Select company size</option>
                {companySizes.map((size) => (
                  <option key={size.value} value={size.value}>
                    {size.label}
                  </option>
                ))}
              </select>
              {errors.companySize && (
                <p className="mt-1 text-sm text-destructive">{errors.companySize}</p>
              )}
            </div>

            <div>
              <label htmlFor="industry" className="block text-sm font-medium text-foreground mb-2">
                Industry <span className="text-destructive">*</span>
              </label>
              <select
                id="industry"
                name="industry"
                value={formData.industry}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.industry ? 'border-destructive' : 'border-input'
                } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
              >
                <option value="">Select industry</option>
                {industries.map((industry) => (
                  <option key={industry} value={industry}>
                    {industry}
                  </option>
                ))}
              </select>
              {errors.industry && (
                <p className="mt-1 text-sm text-destructive">{errors.industry}</p>
              )}
            </div>

            <div>
              <label htmlFor="region" className="block text-sm font-medium text-foreground mb-2">
                Region <span className="text-destructive">*</span>
              </label>
              <select
                id="region"
                name="region"
                value={formData.region}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.region ? 'border-destructive' : 'border-input'
                } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
              >
                <option value="">Select region</option>
                {regions.map((region) => (
                  <option key={region.value} value={region.value}>
                    {region.label}
                  </option>
                ))}
              </select>
              {errors.region && (
                <p className="mt-1 text-sm text-destructive">{errors.region}</p>
              )}
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-display font-semibold text-foreground mb-2">
                AI Strategy & Objectives
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Share your current AI maturity and strategic goals to help us prepare for your consultation.
              </p>
            </div>

            <ConsultationOptions
              selectedOption={formData.consultationType}
              onOptionSelect={handleConsultationTypeChange}
            />

            <div>
              <label htmlFor="aiReadiness" className="block text-sm font-medium text-foreground mb-2">
                Current AI Readiness Level <span className="text-destructive">*</span>
              </label>
              <select
                id="aiReadiness"
                name="aiReadiness"
                value={formData.aiReadiness}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.aiReadiness ? 'border-destructive' : 'border-input'
                } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
              >
                <option value="">Select AI readiness level</option>
                {aiReadinessLevels.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
              {errors.aiReadiness && (
                <p className="mt-1 text-sm text-destructive">{errors.aiReadiness}</p>
              )}
            </div>

            <div>
              <label htmlFor="primaryObjective" className="block text-sm font-medium text-foreground mb-2">
                Primary Strategic Objective <span className="text-destructive">*</span>
              </label>
              <select
                id="primaryObjective"
                name="primaryObjective"
                value={formData.primaryObjective}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.primaryObjective ? 'border-destructive' : 'border-input'
                } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
              >
                <option value="">Select primary objective</option>
                {primaryObjectives.map((objective) => (
                  <option key={objective} value={objective}>
                    {objective}
                  </option>
                ))}
              </select>
              {errors.primaryObjective && (
                <p className="mt-1 text-sm text-destructive">{errors.primaryObjective}</p>
              )}
            </div>

            <div>
              <label htmlFor="timeline" className="block text-sm font-medium text-foreground mb-2">
                Implementation Timeline <span className="text-destructive">*</span>
              </label>
              <select
                id="timeline"
                name="timeline"
                value={formData.timeline}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.timeline ? 'border-destructive' : 'border-input'
                } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
              >
                <option value="">Select implementation timeline</option>
                {timelines.map((timeline) => (
                  <option key={timeline.value} value={timeline.value}>
                    {timeline.label}
                  </option>
                ))}
              </select>
              {errors.timeline && (
                <p className="mt-1 text-sm text-destructive">{errors.timeline}</p>
              )}
            </div>

            <div>
              <label htmlFor="additionalInfo" className="block text-sm font-medium text-foreground mb-2">
                Additional Information (Optional)
              </label>
              <textarea
                id="additionalInfo"
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 resize-none"
                placeholder="Share any specific challenges, questions, or areas of focus for the consultation..."
              ></textarea>
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-display font-semibold text-foreground mb-2">
                Schedule Your Consultation
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Select your preferred date and time. Our team will confirm availability within 24 hours.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="preferredDate" className="block text-sm font-medium text-foreground mb-2">
                  Preferred Date <span className="text-destructive">*</span>
                </label>
                <input
                  type="date"
                  id="preferredDate"
                  name="preferredDate"
                  value={formData.preferredDate}
                  onChange={handleInputChange}
                  min={isHydrated ? new Date().toISOString().split('T')[0] : undefined}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.preferredDate ? 'border-destructive' : 'border-input'
                  } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
                />
                {errors.preferredDate && (
                  <p className="mt-1 text-sm text-destructive">{errors.preferredDate}</p>
                )}
              </div>

              <div>
                <label htmlFor="preferredTime" className="block text-sm font-medium text-foreground mb-2">
                  Preferred Time <span className="text-destructive">*</span>
                </label>
                <input
                  type="time"
                  id="preferredTime"
                  name="preferredTime"
                  value={formData.preferredTime}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.preferredTime ? 'border-destructive' : 'border-input'
                  } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
                />
                {errors.preferredTime && (
                  <p className="mt-1 text-sm text-destructive">{errors.preferredTime}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="timezone" className="block text-sm font-medium text-foreground mb-2">
                Timezone <span className="text-destructive">*</span>
              </label>
              <select
                id="timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
              >
                {timezones.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="bg-muted/50 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <Icon name="InformationCircleIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-2">Consultation Preparation</p>
                  <p>
                    Upon confirmation, you'll receive pre-consultation resources including relevant case studies, 
                    methodology documentation, and a questionnaire to help us maximize the value of your consultation time.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="inline-flex items-center space-x-2 px-6 py-3 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted/80 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="ChevronLeftIcon" size={20} />
            <span>Previous</span>
          </button>

          {currentStep < 4 ? (
            <button
              type="button"
              onClick={handleNext}
              className="inline-flex items-center space-x-2 px-6 py-3 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:shadow-md transition-all duration-200 hover:-translate-y-0.5"
            >
              <span>Next Step</span>
              <Icon name="ChevronRightIcon" size={20} />
            </button>
          ) : (
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center space-x-2 px-8 py-3 text-sm font-medium text-accent-foreground bg-accent rounded-lg hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <span>Submit Inquiry</span>
                  <Icon name="PaperAirplaneIcon" size={20} />
                </>
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default InquiryForm;